(() => {
  if (window.__PACCO_DISCOUNT_VISUAL__) return;
  window.__PACCO_DISCOUNT_VISUAL__ = true;

  const CODE_KEY = 'paccoDiscountCode';
  const CODE_TTL = 15000; // ms
  const metaCache = new Map();
  const moneyFormatter = typeof Intl !== 'undefined' && Intl.NumberFormat
    ? new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' })
    : null;
  const storage = {
    get(key) {
      try {
        return localStorage.getItem(key) || '';
      } catch (_err) {
        return '';
      }
    },
    set(key, value) {
      try {
        localStorage.setItem(key, value);
      } catch (_err) {
        /* noop */
      }
    }
  };
  const state = {
    lastFetchedAt: 0,
    lastCartCode: '',
    lastAppliedCode: '',
    swatchListenerBound: false,
    running: false,
    queued: false
  };
  const hasMutationObserver = typeof MutationObserver !== 'undefined';
  const metaObservers = hasMutationObserver ? new WeakMap() : null;
  const variantItemObservers = hasMutationObserver ? new WeakMap() : null;
  const updatingMetas = hasMutationObserver ? new WeakSet() : null;
  const updatingItems = hasMutationObserver ? new WeakSet() : null;

  const htmlEntityMap = Object.freeze({
    '&quot;': '"',
    '&apos;': "'",
    '&lt;': '<',
    '&gt;': '>',
    '&amp;': '&'
  });

  const normalizeJsonString = (raw) => {
    if (!raw) return '';
    let str = raw.trim();
    if (!str) return '';
    if (str.indexOf('&') !== -1) {
      str = str.replace(/&(?:quot|apos|lt|gt|amp);/g, (entity) => htmlEntityMap[entity] || entity);
    }
    return str;
  };

  const upper = (s) => (s || '').toString().trim().toUpperCase();
  const toInt = (value) => {
    if (value == null) return 0;
    const normalized = String(value).replace(/[^0-9-]/g, '');
    const parsed = Number(normalized);
    return Number.isFinite(parsed) ? parsed : 0;
  };
  const BRL = (cents) => {
    const value = Number(cents || 0) / 100;
    return value.toFixed(2).replace('.', ',');
  };
  const formatMoney = (cents) => {
    const amount = Number(cents || 0) / 100;
    if (Number.isFinite(amount) && moneyFormatter) {
      return moneyFormatter.format(amount);
    }
    return `R$ ${BRL(cents)}`;
  };
  const extractCentsFromText = (text) => {
    if (!text) return 0;
    const normalized = text
      .replace(/\u00a0/g, ' ')
      .replace(/[^0-9,.-]/g, '')
      .replace(/\./g, '')
      .replace(/,(?=\d{0,2}$)/, '.');
    const value = Number(normalized);
    return Number.isFinite(value) ? Math.round(value * 100) : 0;
  };

  const computeOriginalCents = ({ priceCents, compareCents }) => {
    const price = toInt(priceCents);
    const compare = toInt(compareCents);
    if (compare > price) return compare;
    return price || compare;
  };

  const applyDiscountCents = (originalCents, pct) => {
    const base = Number(originalCents || 0);
    const percent = Number(pct || 0);
    if (!Number.isFinite(base) || base <= 0) return 0;
    if (!Number.isFinite(percent) || percent <= 0) return base;
    const discounted = Math.round(base * (100 - percent) / 100);
    return Math.max(0, discounted);
  };

  const readMinInstallmentCents = (contextEl) => {
    const container = contextEl?.closest?.('product-meta') || contextEl;
    const attrValue = container?.getAttribute?.('min-installment-amount');
    let minAmount = toInt(attrValue);
    if (!minAmount) {
      const fromTheme = window.themeVariables?.settings?.minInstallmentAmount;
      if (fromTheme) {
        const numeric = Number(fromTheme);
        if (Number.isFinite(numeric) && numeric > 0) {
          minAmount = Math.round(numeric * 100);
        }
      }
    }
    if (minAmount && minAmount < 100) {
      minAmount = minAmount * 100;
    }
    if (!minAmount) minAmount = 5000;
    return minAmount;
  };

  const readMaxInstallments = (contextEl) => {
    const container = contextEl?.closest?.('product-meta') || contextEl;
    const attrValue = container?.getAttribute?.('max-installments');
    let maxInstallments = Number(attrValue);
    if (!Number.isFinite(maxInstallments) || maxInstallments <= 0) {
      const fromTheme = window.themeVariables?.settings?.maxInstallments;
      if (Number.isFinite(fromTheme) && fromTheme > 0) {
        maxInstallments = Number(fromTheme);
      }
    }
    if (!Number.isFinite(maxInstallments) || maxInstallments <= 0) {
      maxInstallments = 10;
    }
    return maxInstallments;
  };

  const formatInstallmentsText = (totalCents, contextEl) => {
    const minAmount = readMinInstallmentCents(contextEl);
    const maxInstallments = readMaxInstallments(contextEl);
    let installments = Math.floor((Number(totalCents) || 0) / minAmount);
    if (installments > maxInstallments) installments = maxInstallments;
    if (installments < 1) installments = 1;
    const perInstallment = Math.ceil((Number(totalCents) || 0) / installments);
    const amountText = formatMoney(perInstallment);
    return `ou em até ${installments}x ${amountText}\u00A0sem juros`;
  };

  const syncSecondaryPrices = (() => {
    if (typeof window.__PACCO_SYNC_SECONDARY_PRICES === 'function') {
      return window.__PACCO_SYNC_SECONDARY_PRICES;
    }

    const updateStickyPriceDisplay = (currentCents, compareCents) => {
      const stickyPrice = document.querySelector('.product-sticky-form__price');
      if (!stickyPrice) return;

      const currentValue = Number(currentCents) || 0;
      const compareValue = Number(compareCents) || 0;

      let currentEl = stickyPrice.querySelector('[data-sticky-current]');
      if (!currentEl) {
        currentEl = document.createElement('span');
        currentEl.dataset.stickyCurrent = 'true';
        currentEl.className = 'product-sticky-form__price-current price price--highlight';
        stickyPrice.textContent = '';
        stickyPrice.appendChild(currentEl);
      } else {
        currentEl.className = 'product-sticky-form__price-current price price--highlight';
      }
      currentEl.textContent = formatMoney(currentValue);

      const needsCompare = compareValue > currentValue;
      let compareEl = stickyPrice.querySelector('[data-sticky-compare]');
      if (needsCompare) {
        if (!compareEl) {
          compareEl = document.createElement('span');
          compareEl.dataset.stickyCompare = 'true';
          compareEl.className = 'product-sticky-form__price-compare price price--compare';
          compareEl.style.marginInlineStart = '0.5em';
          stickyPrice.appendChild(compareEl);
        } else {
          compareEl.className = 'product-sticky-form__price-compare price price--compare';
          compareEl.style.marginInlineStart = '0.5em';
        }
        compareEl.textContent = formatMoney(compareValue);
        compareEl.style.display = '';
      } else if (compareEl) {
        compareEl.textContent = '';
        compareEl.style.display = 'none';
      }
    };

    const updateQuickBuyPriceDisplay = (variantId, currentCents, compareCents) => {
      const currentValue = Number(currentCents) || 0;
      const compareValue = Number(compareCents) || 0;
      const variantKey = variantId ? String(variantId) : '';
      const hosts = document.querySelectorAll('quick-buy-drawer, quick-buy-popover');

      hosts.forEach((host) => {
        const scriptEl = host.querySelector('script[data-variant]');
        if (variantKey && scriptEl) {
          try {
            const payload = JSON.parse(scriptEl.textContent || '{}');
            const scopedId = payload && payload.id ? String(payload.id) : '';
            if (scopedId && scopedId !== variantKey) return;
          } catch (_err) { /* noop */ }
        }

        host.querySelectorAll('.product-item-meta__price-list-container .price-list').forEach((list) => {
          const nowEl = list.querySelector('.price--highlight') || list.querySelector('.price');
          if (!nowEl) return;
          nowEl.dataset.amount = String(currentValue);
          nowEl.textContent = formatMoney(currentValue);
          // mantém classes originais; tamanhos controlados via CSS específico

          const needsCompare = compareValue > currentValue;
          let compareEl = list.querySelector('.price--compare');
          if (needsCompare) {
            if (!compareEl) {
              compareEl = document.createElement('span');
              compareEl.className = 'price price--compare';
              compareEl.dataset.paccoCompare = 'true';
              list.appendChild(compareEl);
            } else {
              compareEl.className = 'price price--compare';
            }
            compareEl.textContent = formatMoney(compareValue);
            compareEl.style.display = '';
          } else if (compareEl) {
            if (compareEl.dataset.paccoCompare === 'true') {
              compareEl.textContent = '';
              compareEl.style.display = 'none';
            } else {
              compareEl.style.display = 'none';
            }
          }
        });
      });
    };

    const updateQuickBuyMobileDisplay = (variantId, currentCents, compareCents) => {
      const variantKey = variantId ? String(variantId) : '';
      const currentValue = Number(currentCents) || 0;
      const compareValue = Number(compareCents) || 0;
      document.querySelectorAll('[data-quick-buy-price]').forEach((wrap) => {
        if (variantKey) {
          const wrapVariant = wrap.getAttribute('data-variant-id');
          if (wrapVariant && wrapVariant !== variantKey) return;
        }
        const currentEl = wrap.querySelector('.qbbs__price-current');
        if (currentEl) currentEl.textContent = formatMoney(currentValue);
        let compareEl = wrap.querySelector('.qbbs__price-compare');
        const needsCompare = compareValue > currentValue;
        if (needsCompare) {
          if (!compareEl) {
            compareEl = document.createElement('span');
            compareEl.className = 'qbbs__price-compare';
            wrap.appendChild(compareEl);
          }
          compareEl.textContent = formatMoney(compareValue);
          compareEl.style.display = '';
        } else if (compareEl) {
          compareEl.textContent = '';
          compareEl.style.display = 'none';
        }
      });
      document.querySelectorAll('[data-quick-buy-installments]').forEach((node) => {
        if (variantKey) {
          const wrapVariant = node.getAttribute('data-variant-id');
          if (wrapVariant && wrapVariant !== variantKey) return;
        }
        const perInstallment = Math.ceil(currentValue / 10);
        node.textContent = `em até 10x ${formatMoney(perInstallment)} sem juros`;
      });
    };

    const sync = (variantId, currentCents, compareCents) => {
      updateStickyPriceDisplay(currentCents, compareCents);
      updateQuickBuyPriceDisplay(variantId, currentCents, compareCents);
      updateQuickBuyMobileDisplay(variantId, currentCents, compareCents);
    };

    window.__PACCO_SYNC_STICKY_PRICE = updateStickyPriceDisplay;
    window.__PACCO_SYNC_QB_PRICE = updateQuickBuyPriceDisplay;
    window.__PACCO_SYNC_QB_MOBILE = updateQuickBuyMobileDisplay;
    window.__PACCO_SYNC_SECONDARY_PRICES = sync;

    return sync;
  })();

  const getCookie = (name) => {
    const parts = (`; ${document.cookie}`).split(`; ${name}=`);
    if (parts.length < 2) return '';
    const value = parts.pop()?.split(';').shift();
    return value ? decodeURIComponent(value) : '';
  };

  const getClientCode = () => {
    const params = new URLSearchParams(window.location.search);
    const paramCode = params.get('discount') || params.get('discount_code');
    if (paramCode) return upper(paramCode);
    const cookieCode = getCookie('discount_code');
    if (cookieCode) return upper(cookieCode);
    const stored = storage.get(CODE_KEY);
    if (stored) return upper(stored);
    return '';
  };

  const fetchCartCode = async () => {
    try {
      const response = await fetch('/cart.js', { credentials: 'same-origin', cache: 'no-store' });
      if (!response.ok) return '';
      const cart = await response.json();
      const entry = (cart.discount_codes || []).find((item) => item && item.code);
      return entry ? upper(entry.code) : '';
    } catch (_err) {
      return '';
    }
  };

  const resolveCartCode = async () => {
    const now = Date.now();
    if (state.lastFetchedAt && now - state.lastFetchedAt < CODE_TTL) {
      return state.lastCartCode;
    }
    const code = await fetchCartCode();
    state.lastFetchedAt = now;
    if (code) {
      state.lastCartCode = code;
      storage.set(CODE_KEY, code);
    } else {
      state.lastCartCode = '';
    }
    return state.lastCartCode || storage.get(CODE_KEY) || '';
  };

  const parseMeta = (metaJsonStr) => {
    const normalized = normalizeJsonString(metaJsonStr);
    if (!normalized || normalized === 'null' || normalized === 'undefined') return null;
    if (metaCache.has(normalized)) return metaCache.get(normalized);
    try {
      const parsed = JSON.parse(normalized);
      metaCache.set(normalized, parsed);
      return parsed;
    } catch (_err) {
      metaCache.set(normalized, null);
      return null;
    }
  };

  const readPercent = (entry) => {
    if (!entry) return 0;
    if (typeof entry === 'number') return entry > 0 ? entry : 0;
    if (typeof entry !== 'object') {
      const numeric = Number(entry);
      return Number.isFinite(numeric) && numeric > 0 ? numeric : 0;
    }
    const value = entry.percentage ?? entry.PERCENTAGE ?? entry.value ?? entry.VALUE;
    const numeric = Number(value);
    return Number.isFinite(numeric) && numeric > 0 ? numeric : 0;
  };
  const readPercentRecursive = (entry) => {
    const direct = readPercent(entry);
    if (direct) return direct;
    if (entry && typeof entry === 'object') {
      const fromDefault = readPercentRecursive(entry.default || entry.DEFAULT);
      if (fromDefault) return fromDefault;
      const fromValue = readPercentRecursive(entry.value || entry.VALUE);
      if (fromValue) return fromValue;
    }
    return 0;
  };

  const getPctFromMeta = (metaJsonStr, code, variantId) => {
    if (!metaJsonStr || !code) return 0;
    const meta = parseMeta(metaJsonStr);
    if (!meta) return 0;
    const codes = meta.codes || meta.CODES;
    if (!codes) return 0;

    const codeKey = upper(code);
    const entry =
      codes[codeKey] ||
      codes[codeKey.toLowerCase()] ||
      codes[codeKey.replace(/[^A-Z0-9_-]/gi, '')];

    if (!entry) return 0;

    const tryVariant = () => {
      if (!variantId) return 0;
      const map = entry.variants || entry.VARIANTS;
      if (!map) return 0;
      const variantsKeys = [
        String(variantId),
        Number(variantId),
        `gid://shopify/ProductVariant/${variantId}`
      ];
      for (const key of variantsKeys) {
        if (map[key] != null) {
          const pct = readPercentRecursive(map[key]);
          if (pct) return pct;
        }
      }
      return 0;
    };

    const pctVariant = tryVariant();
    if (pctVariant) return pctVariant;

    const pctDefault = readPercentRecursive(entry.default || entry.DEFAULT);
    if (pctDefault) return pctDefault;

    return readPercentRecursive(entry);
  };
  const getAutomaticPct = (metaJsonStr, variantId) => {
    if (!metaJsonStr) return 0;
    const meta = parseMeta(metaJsonStr);
    if (!meta) return 0;
    const automatic = meta.automatic || meta.AUTOMATIC;
    if (!automatic) return 0;

    const variantsMap = automatic.variants || automatic.VARIANTS;
    if (variantsMap && variantId) {
      const variantKeys = [
        String(variantId),
        Number(variantId),
        `gid://shopify/ProductVariant/${variantId}`
      ];
      for (const key of variantKeys) {
        if (variantsMap[key] != null) {
          const pct = readPercentRecursive(variantsMap[key]);
          if (pct) return pct;
        }
      }
    }

    const pctDefault = readPercentRecursive(automatic.default || automatic.DEFAULT);
    if (pctDefault) return pctDefault;

    return readPercentRecursive(automatic);
  };

  const resolveDiscountPriority = ({
    autoProduct = 0,
    autoVariant = 0,
    codeProduct = 0,
    codeVariant = 0,
    codeActive = false
  }) => {
    if (autoVariant > 0) {
      return { pct: autoVariant, source: 'variant-auto' };
    }
    if (codeActive && codeVariant > 0) {
      return { pct: codeVariant, source: 'variant-code' };
    }
    if (autoProduct > 0) {
      return { pct: autoProduct, source: 'product-auto' };
    }
    if (codeActive && codeProduct > 0) {
      return { pct: codeProduct, source: 'product-code' };
    }
    return { pct: 0, source: '' };
  };

  function ensureMetaObserver(meta) {
    if (meta?.hasAttribute?.('data-pkb-kit')) return;
    if (!hasMutationObserver || !meta || metaObservers.has(meta)) return;
    const list = meta.querySelector('.my-price-list-fix');
    if (!list) return;
    const observer = new MutationObserver(() => {
      if (updatingMetas && updatingMetas.has(meta)) return;
      applyDiscountToProductMeta(meta, state.lastAppliedCode);
    });
    observer.observe(list, { childList: true, characterData: true, subtree: true });
    metaObservers.set(meta, observer);
  }

  function ensureVariantItemObserver(item) {
    if (!hasMutationObserver || !item || variantItemObservers.has(item)) return;
    const priceBox = item.querySelector('.vi-price');
    if (!priceBox) return;
    const observer = new MutationObserver(() => {
      if (updatingItems && updatingItems.has(item)) return;
      applyDiscountToVariantItem(item, state.lastAppliedCode);
    });
    observer.observe(priceBox, { childList: true, characterData: true, subtree: true });
    variantItemObservers.set(item, observer);
  }

  const ensureOriginalDataset = (element, { amountKey = 'origAmount', textKey = 'origText' } = {}) => {
    if (!element) return;
    if (!element.dataset[amountKey]) {
      const candidate = element.dataset.amount || extractCentsFromText(element.textContent || '') || '';
      element.dataset[amountKey] = candidate !== '' ? String(candidate) : '';
    }
    if (!element.dataset[textKey]) {
      element.dataset[textKey] = element.textContent || '';
    }
  };

  const restorePriceSpan = (element, { removeIfAdded = false } = {}) => {
    if (!element) return;
    const amount = element.dataset.origAmount;
    const text = element.dataset.origText;
    if (removeIfAdded && element.dataset.addedByDiscount === 'true') {
      element.remove();
      return;
    }
    if (amount) element.dataset.amount = amount;
    if (text != null) {
      element.textContent = text;
    } else if (amount) {
      element.textContent = formatMoney(amount);
    }
    element.style.removeProperty('display');
  };

  const restoreTextNode = (element) => {
    if (!element) return;
    if (element.dataset.origText != null) {
      element.textContent = element.dataset.origText;
    }
  };

  const updateLabelVisibility = (container) => {
    if (!container) return;
    const hasVisibleChild = Array.from(container.children || []).some((node) => {
      if (!node || node.nodeType !== 1) return false;
      return node.style.display !== 'none';
    });
    container.classList.toggle('is-empty', !hasVisibleChild);
  };

  const hasAutomaticData = (variantStr, productStr) => {
    const hint = (str) => {
      if (typeof str !== 'string') return false;
      return str.indexOf('"automatic"') !== -1 || str.indexOf('&quot;automatic&quot;') !== -1;
    };
    return hint(variantStr) || hint(productStr);
  };

  const applyDiscountToProductMeta = (meta, code) => {
    if (!meta) return;
    if (meta.hasAttribute('data-pkb-kit')) return;
    
    // Se o pacco_config já aplicou preço promocional, não sobrescrever
    const discountSource = meta.dataset?.discountSource || meta.getAttribute?.('data-discount-source') || '';
    if (discountSource === 'pacco-config-promotion') return;
    
    // Verificar se a VARIANTE ATUAL tem show_promotion=true no pacco_config
    const paccoConfigRaw = meta.dataset?.paccoConfig || meta.getAttribute?.('data-pacco-config') || '';
    if (paccoConfigRaw) {
      try {
        const paccoConfig = JSON.parse(paccoConfigRaw);
        if (paccoConfig && typeof paccoConfig === 'object') {
          // Detectar variante atual
          const variantInput = meta.querySelector('form[action*="/cart/add"] input[name="id"]');
          const variantId = variantInput?.value || meta.getAttribute('data-variant-id') || '';
          
          // Detectar cor da variante atual
          let colorOpt = null;
          const scriptEl = meta.querySelector('script[data-variant]') || document.querySelector('script[data-variant]');
          if (scriptEl) {
            try {
              const v = JSON.parse(scriptEl.textContent || '{}');
              // Tentar pegar option1 como cor (mais comum)
              colorOpt = v.option1 || v.title || null;
              // Se tiver options array, usar
              if (v.options && v.options.length > 0) {
                colorOpt = v.options[0];
              }
            } catch (_) {}
          }
          
          // Verificar scope da variante atual
          let currentScope = null;
          if (colorOpt) {
            const colorKey = 'cor:' + colorOpt;
            currentScope = paccoConfig[colorKey];
          }
          // Fallback: buscar por variant_id
          if (!currentScope && variantId) {
            const keys = Object.keys(paccoConfig);
            for (let i = 0; i < keys.length; i++) {
              const key = keys[i];
              if (key === 'global' || key === '_version' || key === '_updatedAt') continue;
              const s = paccoConfig[key];
              if (s && s.variant_id) {
                const scopeVarId = String(s.variant_id).replace('gid://shopify/ProductVariant/', '');
                if (scopeVarId === String(variantId)) {
                  currentScope = s;
                  break;
                }
              }
            }
          }
          
          // Se a variante ATUAL tem promoção, deixar o product-info.liquid gerenciar
          if (currentScope && currentScope.settings && currentScope.settings.show_promotion === true) {
            return;
          }
        }
      } catch (_e) { /* noop */ }
    }
    
    const hasVariantMeta =
      meta.dataset?.variantDiscountPricing ||
      meta.getAttribute?.('data-variant-discount-pricing') ||
      meta.dataset?.targetDescontosVariant ||
      meta.getAttribute?.('data-target-descontos-variant');
    if (hasVariantMeta) return; // não sobrescreve valores vindos da variante/metafield
    
    // Lê addon_price do metacampo (fixo por produto)
    const addonPriceAttr = Number(meta.dataset?.addonPrice || meta.getAttribute?.('data-addon-price')) || 0;
    
    // Se tem addon, verifica se já foi aplicado antes de continuar
    if (addonPriceAttr > 0) {
      const priceEl = meta.querySelector('.my-price-fix--large, .my-price-fix--highlight');
      if (priceEl && priceEl.dataset.addonApplied === 'true') {
        return; // Addon já gerenciado pelo product-info.liquid
      }
    }
    
    ensureMetaObserver(meta);
    if (hasMutationObserver && updatingMetas) {
      if (updatingMetas.has(meta)) return;
      updatingMetas.add(meta);
    }
    try {
      const list = meta.querySelector('.my-price-list-fix');
      if (!list) return;

      const nowEl = list.querySelector('.my-price-fix--highlight') || list.querySelector('.my-price-fix--large');
      if (!nowEl) return;

      ensureOriginalDataset(nowEl);

      let compareEl = list.querySelector('.my-price-fix--compare');
      if (!compareEl) {
        compareEl = document.createElement('span');
        compareEl.className = 'my-price-fix my-price-fix--compare';
        compareEl.dataset.addedByDiscount = 'true';
        list.appendChild(compareEl);
      }
      ensureOriginalDataset(compareEl);

      // Garante que o addon está incluído nos valores originais
      let baseAmount = toInt(nowEl.dataset.origAmount || nowEl.dataset.amount);
      const compareBaseRaw = compareEl ? (compareEl.dataset.origAmount || compareEl.dataset.amount) : '';
      let compareBase = toInt(compareBaseRaw);
      
      // Se o addon ainda não foi somado aos valores originais, soma agora
      if (addonPriceAttr > 0 && nowEl.dataset.addonApplied !== 'true') {
        baseAmount += addonPriceAttr;
        if (compareBase > 0) compareBase += addonPriceAttr;
        nowEl.dataset.origAmount = String(baseAmount);
        nowEl.dataset.addonApplied = 'true';
        if (compareEl && compareBase > 0) {
          compareEl.dataset.origAmount = String(compareBase);
        }
      }

      const installmentsEl = meta.querySelector('.payment-ways--installments .payment-ways__text');
      if (installmentsEl && !installmentsEl.dataset.origText) {
        installmentsEl.dataset.origText = installmentsEl.textContent || '';
      }

      const loyaltyPointsEl = meta.querySelector('.pacco-loyalty-card__points');
      if (loyaltyPointsEl && !loyaltyPointsEl.dataset.origText) {
        loyaltyPointsEl.dataset.origText = loyaltyPointsEl.textContent || '';
      }

      const metaJsonStr = meta.getAttribute('data-target-descontos') || '';
      const metaVariantStr = meta.getAttribute('data-target-descontos-variant') || metaJsonStr;
      const metaProductStr = meta.getAttribute('data-target-descontos-product') || '';
      const variantInput = meta.querySelector('form[action*="/cart/add"] input[name="id"]');
      const variantId = variantInput?.value || meta.getAttribute('data-variant-id') || '';
      const codeActive = !!code;
      const autoProduct = getAutomaticPct(metaProductStr, variantId) || 0;
      const autoVariant = getAutomaticPct(metaVariantStr, variantId) || 0;
      const codeProduct = codeActive ? (getPctFromMeta(metaProductStr, code, variantId) || 0) : 0;
      const codeVariant = codeActive ? (getPctFromMeta(metaVariantStr, code, variantId) || 0) : 0;
      const { pct, source } = resolveDiscountPriority({
        autoProduct,
        autoVariant,
        codeProduct,
        codeVariant,
        codeActive
      });
      if (source) {
        meta.dataset.discountSource = source;
      } else {
        delete meta.dataset.discountSource;
      }

      const automaticHint = hasAutomaticData(metaVariantStr, metaProductStr);

      if (!pct && !codeActive && automaticHint) {
        return;
      }

      let originalPrice = computeOriginalCents({ priceCents: baseAmount, compareCents: compareBase });
      if (!originalPrice) originalPrice = baseAmount || compareBase || 0;

      let expectedNow = baseAmount || originalPrice || 0;
      let compareExpectedAmount = compareBase;
      let compareShouldBeVisible = compareBase > expectedNow;

      if (pct > 0) {
        if (!originalPrice) return;
        const discountedPrice = applyDiscountCents(originalPrice, pct);
        if (!discountedPrice) return;
        expectedNow = discountedPrice;
        compareExpectedAmount = originalPrice;
        compareShouldBeVisible = originalPrice > discountedPrice;
      }

      const signature = `${codeActive ? upper(code) : ''}|${pct}|${originalPrice}|${source}`;
      const currentNow = toInt(nowEl.dataset.amount || extractCentsFromText(nowEl.textContent || ''));
      const compareCurrent = compareEl
        ? toInt(compareEl.dataset.amount || extractCentsFromText(compareEl.textContent || ''))
        : 0;
      const compareVisible = !!(compareEl && compareEl.style.display !== 'none' && (compareEl.textContent || '').trim());
      const currentNowText = extractCentsFromText(nowEl.textContent || '');
      const compareCurrentText = compareEl ? extractCentsFromText(compareEl.textContent || '') : 0;
      const amountsMatch = currentNow === expectedNow && currentNowText === expectedNow;
      const compareMatch = compareShouldBeVisible
        ? (compareVisible && compareCurrent === compareExpectedAmount && compareCurrentText === compareExpectedAmount)
        : !compareVisible;
      const signatureMatch = meta.dataset.discountSignature === signature;
      const shouldUpdatePrices = !(signatureMatch && amountsMatch && compareMatch);

      const labelWrap = meta.querySelector('.my-label-list-fix');
      const saleLabels = labelWrap
        ? labelWrap.querySelectorAll('[data-sale-label="true"]')
        : null;

      if (pct <= 0) {
        if (shouldUpdatePrices) {
          restorePriceSpan(compareEl, { removeIfAdded: true });
          restorePriceSpan(nowEl);
          const label = labelWrap?.querySelector('[data-discount-label]');
          if (label) label.remove();
          if (saleLabels) {
            saleLabels.forEach((el) => {
              el.style.removeProperty('display');
              if (el.dataset.saleOriginalDisplay != null) {
                delete el.dataset.saleOriginalDisplay;
              }
            });
          }
          updateLabelVisibility(labelWrap);
        }
      } else {
      if (shouldUpdatePrices) {
        const original = originalPrice;
        const discounted = expectedNow;

        nowEl.dataset.amount = String(discounted);
        nowEl.textContent = formatMoney(discounted);

        if (compareEl) {
          compareEl.dataset.amount = String(original);
          compareEl.textContent = formatMoney(original);
          compareEl.style.display = compareShouldBeVisible ? '' : 'none';
        }

        if (labelWrap) {
          if (saleLabels) {
            saleLabels.forEach((el) => {
              if (el.dataset.saleOriginalDisplay == null) {
                el.dataset.saleOriginalDisplay = el.style.display || '';
              }
              el.style.display = 'none';
            });
          }
          let badge = labelWrap.querySelector('[data-discount-label]');
          if (!badge) {
            badge = document.createElement('span');
            badge.className = 'my-label-fix';
            badge.dataset.discountLabel = 'true';
            labelWrap.appendChild(badge);
          }
          const pctText = String(pct).replace('.', ',');
          badge.textContent = `REDUZIDO EM ${pctText}%`;
        }
        updateLabelVisibility(labelWrap);
      }
      }

      const installmentsExpected = formatInstallmentsText(expectedNow, meta);
      if (installmentsEl && installmentsEl.textContent !== installmentsExpected) {
        installmentsEl.textContent = installmentsExpected;
      }
      if (installmentsEl) {
        installmentsEl.dataset.origText = installmentsExpected;
      }

      const pointsExpected = String(Math.max(0, Math.floor(expectedNow / 100)));
      if (loyaltyPointsEl && loyaltyPointsEl.textContent !== pointsExpected) {
        loyaltyPointsEl.textContent = pointsExpected;
      }
      if (loyaltyPointsEl) {
        loyaltyPointsEl.dataset.origText = pointsExpected;
      }

      const compareForSync = compareShouldBeVisible ? compareExpectedAmount : 0;
      syncSecondaryPrices(String(variantId || ''), expectedNow, compareForSync);

      meta.dataset.discountSignature = signature;
    } finally {
      if (hasMutationObserver && updatingMetas) {
        updatingMetas.delete(meta);
      }
    }
  };

  const applyDiscountToVariantItem = (item, code) => {
    if (!item) return;
    ensureVariantItemObserver(item);
    if (hasMutationObserver && updatingItems) {
      if (updatingItems.has(item)) return;
      updatingItems.add(item);
    }
    try {
      const priceBox = item.querySelector('.vi-price');
      if (!priceBox) return;

      const nowEl = priceBox.querySelector('.vi-now');
      if (nowEl) ensureOriginalDataset(nowEl);

      let wasEl = priceBox.querySelector('.vi-was');
      if (!wasEl) {
        wasEl = document.createElement('span');
        wasEl.className = 'vi-was';
        wasEl.dataset.addedByDiscount = 'true';
        priceBox.appendChild(wasEl);
      }
      ensureOriginalDataset(wasEl);

      const installmentsEl = item.querySelector('.vi-install');
      if (installmentsEl && !installmentsEl.dataset.origText) {
        installmentsEl.dataset.origText = installmentsEl.textContent || '';
      }

      const labelWrap = item.querySelector('.vi-labels');
      const saleLabels = labelWrap
        ? labelWrap.querySelectorAll('[data-sale-label="true"]')
        : null;

      const metaJsonStr = item.getAttribute('data-target-descontos') || '';
      const metaVariantStr = item.getAttribute('data-target-descontos-variant') || metaJsonStr;
      const metaProductStr = item.getAttribute('data-target-descontos-product') || '';
      const activeSwatch = item.querySelector('.vi-swatch.is-active') || item.querySelector('.vi-swatch');
      const variantId = activeSwatch?.dataset.vid || '';
      const codeActive = !!code;
      const autoProduct = getAutomaticPct(metaProductStr, variantId) || 0;
      const autoVariant = getAutomaticPct(metaVariantStr, variantId) || 0;
      const codeProduct = codeActive ? (getPctFromMeta(metaProductStr, code, variantId) || 0) : 0;
      const codeVariant = codeActive ? (getPctFromMeta(metaVariantStr, code, variantId) || 0) : 0;
      const { pct, source } = resolveDiscountPriority({
        autoProduct,
        autoVariant,
        codeProduct,
        codeVariant,
        codeActive
      });
      if (source) {
        item.dataset.discountSource = source;
      } else {
        delete item.dataset.discountSource;
      }

      const inlineSignature = item.dataset.viInlineSignature;
      if (!codeActive && inlineSignature && pct > 0) {
        return;
      }

      const automaticHint = hasAutomaticData(metaVariantStr, metaProductStr);

      if (!pct && !codeActive && automaticHint) {
        return;
      }

      const baselinePrice = (() => {
        if (item.dataset.origPrice) return toInt(item.dataset.origPrice);
        const priceCents = activeSwatch?.dataset.price || nowEl?.dataset.amount;
        const cents = toInt(priceCents || (nowEl ? extractCentsFromText(nowEl.textContent || '') : 0));
        if (cents) item.dataset.origPrice = String(cents);
        return cents;
      })();

      const baselineCompare = (() => {
        if (item.dataset.origCompare) return toInt(item.dataset.origCompare);
        const compareCents = activeSwatch?.dataset.compare || wasEl?.dataset.amount;
        const cents = toInt(compareCents || (wasEl ? extractCentsFromText(wasEl.textContent || '') : 0));
        if (cents) item.dataset.origCompare = String(cents);
        return cents;
      })();

      let originalPrice = computeOriginalCents({ priceCents: baselinePrice, compareCents: baselineCompare });
      if (!originalPrice) originalPrice = baselinePrice || baselineCompare || 0;

      // Ler addon do pacco config (personalização)
      const addonCents = toInt(item.dataset.addonPrice || item.getAttribute('data-addon-price') || 0);

      let expectedNow = baselinePrice || originalPrice || 0;
      let compareExpectedAmount = baselineCompare;
      let compareShouldBeVisible = baselineCompare > expectedNow;

      if (pct > 0) {
        if (!originalPrice) return;
        const discountedPrice = applyDiscountCents(originalPrice, pct);
        if (!discountedPrice) return;
        expectedNow = discountedPrice;
        compareExpectedAmount = originalPrice;
        compareShouldBeVisible = originalPrice > discountedPrice;
      }

      // Adicionar addon ao preço final (desconto aplicado na base, addon em cima)
      if (addonCents > 0) {
        expectedNow += addonCents;
        if (compareExpectedAmount > 0) {
          compareExpectedAmount += addonCents;
        }
      }
      // Recalcular visibilidade do compare após adicionar addon
      compareShouldBeVisible = compareExpectedAmount > expectedNow;

      const signature = `${codeActive ? upper(code) : ''}|${pct}|${baselinePrice}|${addonCents}|${source}`;
      const currentNow = nowEl ? toInt(nowEl.dataset.amount || extractCentsFromText(nowEl.textContent || '')) : 0;
      const compareCurrent = wasEl
        ? toInt(wasEl.dataset.amount || extractCentsFromText(wasEl.textContent || ''))
        : 0;
      const compareVisible = !!(wasEl && wasEl.style.display !== 'none' && (wasEl.textContent || '').trim());
      const currentNowText = nowEl ? extractCentsFromText(nowEl.textContent || '') : 0;
      const compareCurrentText = wasEl ? extractCentsFromText(wasEl.textContent || '') : 0;
      const signatureMatch = item.dataset.discountSignature === signature;
      const amountsMatch = currentNow === expectedNow && currentNowText === expectedNow;
      const compareMatch = compareShouldBeVisible
        ? (compareVisible && compareCurrent === compareExpectedAmount && compareCurrentText === compareExpectedAmount)
        : !compareVisible;
      const shouldUpdatePrices = !(signatureMatch && amountsMatch && compareMatch);

      if (pct <= 0) {
        if (shouldUpdatePrices) {
          restorePriceSpan(wasEl, { removeIfAdded: wasEl?.dataset.addedByDiscount === 'true' });
          // Se tem addon, setar o preço completo ao invés de restaurar o original
          if (addonCents > 0 && nowEl) {
            nowEl.dataset.amount = String(expectedNow);
            nowEl.dataset.origAmount = String(expectedNow);
            nowEl.dataset.origText = formatMoney(expectedNow);
            nowEl.textContent = formatMoney(expectedNow);
          } else {
            restorePriceSpan(nowEl);
          }
        }
      } else {
        if (shouldUpdatePrices) {
          if (nowEl) {
            nowEl.dataset.amount = String(expectedNow);
            nowEl.textContent = formatMoney(expectedNow);
          }
          if (wasEl) {
            wasEl.dataset.amount = String(originalPrice);
            wasEl.textContent = formatMoney(originalPrice);
            wasEl.style.display = compareShouldBeVisible ? '' : 'none';
          }
        }
      }

      if (labelWrap) {
        if (saleLabels) {
          saleLabels.forEach((el) => {
            if (el.dataset.saleOriginalDisplay != null) {
              const originalDisplay = el.dataset.saleOriginalDisplay;
              delete el.dataset.saleOriginalDisplay;
              if (originalDisplay) {
                el.style.display = originalDisplay;
              } else {
                el.style.removeProperty('display');
              }
            } else {
              el.style.removeProperty('display');
            }
          });
        }
        const discountBadge = labelWrap.querySelector('[data-discount-label]');
        if (discountBadge) discountBadge.remove();
        updateLabelVisibility(labelWrap);
      }

      if (installmentsEl) {
        const installmentsExpected = formatInstallmentsText(expectedNow, item);
        if (installmentsEl.textContent !== installmentsExpected) {
          installmentsEl.textContent = installmentsExpected;
        }
        installmentsEl.dataset.origText = installmentsExpected;
      }

      item.dataset.discountSignature = signature;
    } finally {
      if (hasMutationObserver && updatingItems) {
        updatingItems.delete(item);
      }
    }
  };

  const renderPDP = (code) => {
    document.querySelectorAll('product-meta[data-target-descontos]').forEach((meta) => {
      applyDiscountToProductMeta(meta, code);
    });
  };

  const bindSwatchHandler = () => {
    if (state.swatchListenerBound) return;
    document.addEventListener('click', (event) => {
      const button = event.target.closest('.vi-swatch[data-vi-select]');
      if (!button) return;
      const item = button.closest('variant-item[data-target-descontos]');
      if (!item) return;
      requestAnimationFrame(() => {
        applyDiscountToVariantItem(item, state.lastAppliedCode);
      });
    });
    state.swatchListenerBound = true;
  };

  const renderGrid = (code) => {
    bindSwatchHandler();
    document.querySelectorAll('variant-item[data-target-descontos]').forEach((item) => {
      applyDiscountToVariantItem(item, code);
    });
  };

  const runInternal = (code) => {
    state.lastAppliedCode = upper(code || '');
    renderPDP(state.lastAppliedCode);
    renderGrid(state.lastAppliedCode);
  };

  const scheduleRun = () => {
    if (state.running) {
      state.queued = true;
      return;
    }
    state.running = true;
    (async () => {
      try {
        const clientCode = getClientCode();
        const storedCode = upper(storage.get(CODE_KEY) || '');

        if (clientCode) {
          storage.set(CODE_KEY, clientCode);
          if (clientCode !== state.lastAppliedCode) {
            runInternal(clientCode);
          }
        }

        // Evita sondagem de /cart.js em cold start sem qualquer contexto de desconto.
        // Se houver query param, cookie ou storage, seguimos verificando normalmente.
        const shouldProbeCartCode = !clientCode && !!(storedCode || state.lastCartCode);
        const cartCode = shouldProbeCartCode ? await resolveCartCode() : '';
        const finalCode = clientCode || cartCode || storedCode || '';

        if (!finalCode) {
          state.lastCartCode = '';
          if (state.lastAppliedCode) {
            runInternal('');
          } else {
            renderGrid('');
          }
          state.lastAppliedCode = '';
          return;
        }

        if (finalCode !== state.lastAppliedCode) {
          runInternal(finalCode);
        } else if (state.lastAppliedCode) {
          renderGrid(state.lastAppliedCode);
        }
      } finally {
        state.running = false;
        if (state.queued) {
          state.queued = false;
          scheduleRun();
        }
      }
    })();
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', scheduleRun);
  } else {
    scheduleRun();
  }

  document.addEventListener('product:rerender', scheduleRun);
  document.addEventListener('facet:afterRender', scheduleRun);
  document.addEventListener('facet:criteria-changed', scheduleRun);
  const scheduleRunAsync = () => setTimeout(scheduleRun, 0);
  document.addEventListener('collection:items:added', scheduleRunAsync);
  document.addEventListener('collection:refreshed', scheduleRun);
  document.addEventListener('facets:updated', scheduleRunAsync);
  document.addEventListener('variant:change', scheduleRun);
  document.addEventListener('variant:changed', scheduleRun);
  document.addEventListener('openable-element:load:end', scheduleRun);
  document.addEventListener('openable-element:open', scheduleRun);
  document.documentElement.addEventListener('cart:refresh', scheduleRun);
  window.addEventListener('focus', () => setTimeout(scheduleRun, 0));

})();
